/**
 * Shared schema primitives (DATA_SCHEMAS §0) — the reusable zod building blocks
 * that units, enemies, heroes and levels compose from. One source of truth: TS
 * types are inferred from these schemas (no hand-written duplicates).
 */
import { z } from 'zod';

export const Vec2Schema = z.object({ x: z.number(), y: z.number() });
export type Vec2 = z.infer<typeof Vec2Schema>;

export const DamageTypeSchema = z.enum(['physical', 'magic', 'true']);
export type DamageType = z.infer<typeof DamageTypeSchema>;

export const StatusTypeSchema = z.enum([
  'slow',
  'freeze',
  'stun',
  'burn',
  'poison',
  'bleed',
  'mark',
  'sunder',
]);
export type StatusType = z.infer<typeof StatusTypeSchema>;

export const SlotTypeSchema = z.enum(['ground', 'tower', 'support']);
export type SlotType = z.infer<typeof SlotTypeSchema>;

export const TargetModeSchema = z.enum([
  'first',
  'last',
  'strongest',
  'nearest',
  'flyer',
]);
export type TargetMode = z.infer<typeof TargetModeSchema>;

/** Unit families (DATA_SCHEMAS §3). Masteries & relic affixes scale by family. */
export const UnitFamilySchema = z.enum([
  'melee',
  'rangedPhysical',
  'magic',
  'support',
]);
export type UnitFamily = z.infer<typeof UnitFamilySchema>;

/**
 * A partial stat block: every field optional so it can express both a full
 * actor (enemy) and a sparse upgrade-tier override that merges onto the
 * previous tier. Defaults are resolved at runtime in `battle/Stats.ts`
 * (omitted = 0/default), so we deliberately do NOT apply defaults here —
 * that would erase the "unspecified" information tier-merging relies on.
 */
export const StatBlockSchema = z
  .object({
    hp: z.number().nonnegative(),
    /** The single defensive stat: % damage mitigation (all damage types). */
    armor: z.number().min(0).max(0.95),
    damage: z.number().nonnegative(),
    /** Flavor + a hook for future type-immunities (anti-magic, arrow-deflect…);
     *  it no longer selects a separate defense channel. */
    damageType: DamageTypeSchema,
    armorPierce: z.number().min(0).max(1),
    range: z.number().nonnegative(),
    attackSpeed: z.number().nonnegative(),
    moveSpeed: z.number().nonnegative(),
    critChance: z.number().min(0).max(1),
    critMult: z.number().min(1),
    canHitFlyers: z.boolean(),
    /** Chain lightning: number of EXTRA enemies the attack jumps to (0 = single). */
    chainCount: z.number().int().nonnegative(),
    /** Multi-shot: independent targets hit per volley (1 = normal; 2 = two archers). */
    multiShot: z.number().int().positive(),
  })
  .partial();
export type PartialStatBlock = z.infer<typeof StatBlockSchema>;

/** A status application carried by an attack/ability (DATA_SCHEMAS §0). */
export const StatusEffectSchema = z.object({
  type: StatusTypeSchema,
  magnitude: z.number(), // slow 0.4 = -40% speed; burn 12 = dmg/sec
  durationS: z.number().positive(),
});
export type StatusEffect = z.infer<typeof StatusEffectSchema>;
